<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: null,
                    },
                    {
                        name: $tc('messages.grades', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="md:flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    v-if="activity.max_score !== 10"
                                >
                                    {{ $t('messages.correct_number') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    v-if="activity.max_score !== 10"
                                >
                                    {{ $tc('messages.grades', 1) }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    v-if="activity.max_score !== 10"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    v-if="activity.max_score !== 10"
                                ></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(student, index) in students"
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                class="hover:bg-gray-200"
                                :key="student.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ student.full_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span v-if="student.done">
                                        {{ student.correct_answers }} /
                                        {{ activity.questions_count }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <BadgePill>
                                        {{ student.letter_grade }}
                                    </BadgePill>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <div class="block md:inline-block">
                                        {{ student.diff_for_humans }}
                                    </div>
                                    <span class="text-xs text-gray-600">
                                        {{ student.date_time }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <InertiaSquareConfirmActionButton
                                        :href="student.links.attempt_delete"
                                        danger
                                        :confirm-text="$t('messages.delete')"
                                        method="delete"
                                        v-if="student.links.attempt_delete"
                                    >
                                        <template v-slot:icon>
                                            <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                        </template>
                                        {{ $t('messages.are_you_sure?') }}
                                        <br />
                                        {{ student.delete_attempt_message }}
                                    </InertiaSquareConfirmActionButton>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="md:grid md:grid-cols-2 md:gap-6 mt-5">
            <div
                v-for="question in questions"
                :key="question.question_id"
                class="rounded-lg border-2 border-gray-300 py-2 px-3 shadow-lg col-span-2"
            >
                <div class="font-semibold text-gray-900">
                    {{ question.title }}
                </div>
                <div v-if="question.description">
                    <QuestionDescriptionDisplay :question="question" />
                </div>
                <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.image_path">
                    <img :src="question.image_path" alt="cover" class="h-auto" />
                </div>
                <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.shared_image">
                    <img :src="question.shared_image" alt="cover" class="h-auto" />
                </div>
                <component :is="question.type" :question="question" :students="students" />
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import AppButton from '@/components/AppButton.vue';
import QuestionCreateFormPanel from '@/components/Quiz/QuestionCreateFormPanel.vue';
import BadgePill from '@/components/BadgePill.vue';
import SmallNumberRangeIndicator from '@/components/SmallNumberRangeIndicator.vue';
import CorrectIncorrectIcon from '@/components/Quiz/CorrectIncorrectIcon.vue';
import StudentsProfileStackFiltered from '@/components/StudentsProfileStackFiltered.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import QuestionGeneralReview from '@/components/Quiz/Review/QuestionGeneralReview.vue';
import QuestionOrderElementsReview from '@/components/Quiz/Review/QuestionOrderElementsReview.vue';
import QuestionDescriptionDisplay from '@/components/Quiz/QuestionDescriptionDisplay.vue';

export default {
    components: {
        QuestionDescriptionDisplay,
        FormTextarea,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        AppButton,
        QuestionCreateFormPanel,
        BadgePill,
        SmallNumberRangeIndicator,
        CorrectIncorrectIcon,
        StudentsProfileStackFiltered,
        InertiaSquareConfirmActionButton,
        'multiple-choice': QuestionGeneralReview,
        'short-answer': QuestionGeneralReview,
        'true-false': QuestionGeneralReview,
        'order-phrase': QuestionGeneralReview,
        'order-elements': QuestionOrderElementsReview,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'options', 'questions', 'activity', 'students'],
    data() {
        return {
            show: 'score',
            displayOptions: [
                { text: this.$tc('messages.scores', 2), value: 'score' },
                { text: this.$tc('messages.letters', 2), value: 'letter' },
            ],
        };
    },
    methods: {},
};
</script>
